import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  MessageCircle, 
  Brain, 
  Leaf, 
  Heart, 
  School, 
  Lightbulb, 
  Home,
  CheckCircle,
  Calendar,
  Users,
  BookOpen,
  Target,
  Phone,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/cNibIU3n28Bpe5C5TWb7y00';

const NewLanding = () => {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [loginOpen, setLoginOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [discoveryForm, setDiscoveryForm] = useState({
    parent_name: '',
    email: '',
    phone: '',
    child_name: '',
    child_age: '',
    main_concerns: '',
    best_time_to_call: '',
    heard_about_us: ''
  });

  React.useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Smooth scroll
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await API.post('/auth/login', loginData);
      login(response.data.user, response.data.token);
      toast.success('Welcome back!');
      // Redirect based on role
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoveryCall = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/discovery-calls`, {
        ...discoveryForm,
        child_age: parseInt(discoveryForm.child_age)
      });
      toast.success('Request submitted! Robin will reach out to schedule your discovery call.');
      setDiscoveryForm({
        parent_name: '',
        email: '',
        phone: '',
        child_name: '',
        child_age: '',
        main_concerns: '',
        best_time_to_call: '',
        heard_about_us: ''
      });
      setDiscoveryOpen(false);
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pillars = [
    {
      icon: MessageCircle,
      title: 'Communication',
      description: 'Understand verbal and nonverbal communication, reduce frustration, strengthen connection and language development at home'
    },
    {
      icon: Brain,
      title: 'Behavior',
      description: 'Move beyond surface-level fixes, uncover root causes, build a regulated and supportive family environment'
    },
    {
      icon: Leaf,
      title: 'Sensory',
      description: 'Understanding and supporting all eight senses — tactile, auditory, visual, olfactory, gustatory, vestibular, proprioceptive, and interoceptive — including food/diet as a sensory experience, retained primitive reflexes and their impact on behavior, learning, and daily function'
    },
    {
      icon: Heart,
      title: 'Health & Wellness',
      description: 'Reduce environmental and dietary toxins, build nourishment strategies and routines that support the body and brain to function optimally'
    },
    {
      icon: School,
      title: 'Academic & IEP Support',
      description: 'Navigate the school system, understand the IEP process, and feel more confident preparing for meetings and requesting supports'
    },
    {
      icon: Lightbulb,
      title: 'Therapy Navigation',
      description: 'Guidance on understanding traditional and non-traditional approaches like ILS, visual exercises, and primitive reflex integration — helping you make informed decisions for your family'
    },
    {
      icon: Home,
      title: 'Daily Life Strategies',
      description: 'Actionable strategies for routines, transitions, restricted eating, meltdowns, sleep, diet and real everyday family life'
    },
    {
      icon: Users,
      title: 'Family & Emotional Wellness',
      description: 'Supporting the whole family — managing caregiver burnout, strengthening sibling dynamics, nurturing your marriage, and building a sustainable support system'
    },
    {
      icon: Target,
      title: 'Safety & Independence',
      description: 'Building life skills, addressing elopement and safety concerns, fostering age-appropriate independence, and planning for your child\'s long-term growth'
    }
  ];

  const credentials = [
    { label: 'Autism Coach & Consultant', sublabel: 'SLP-A background with decades of experience in pediatric and school settings' },
    { label: 'Master\'s in Special Education', sublabel: 'IEP navigation, school systems, understanding processes as a parent' },
    { label: 'Integrative & Out-of-the-Box Approaches', sublabel: 'Primitive reflexes, visual exercises, experience implementing ILS, traditional and non-traditional therapy knowledge, sensory integration' },
    { label: 'Autism Mom of a 10-Year-Old', sublabel: 'Real-world navigation of schools, services, health, and daily life' },
    { label: 'Neurodivergent Family', sublabel: 'Autism · ADHD · lived experience across the spectrum' }
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-white/80 border-b border-[#1E2D3D]/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#1E2D3D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Unbox'D Autism
              </h1>
              <p className="text-xs uppercase tracking-wider text-[#C97B5A]">Coaching & Consulting</p>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('program')} className="text-[#1E2D3D] hover:text-[#C97B5A] transition-colors">
                Program
              </button>
              <button onClick={() => scrollToSection('community')} className="text-[#1E2D3D] hover:text-[#C97B5A] transition-colors">
                Community
              </button>
              <button onClick={() => scrollToSection('app-tools')} className="text-[#1E2D3D] hover:text-[#C97B5A] transition-colors">
                App Tools
              </button>
              <button onClick={() => scrollToSection('about')} className="text-[#1E2D3D] hover:text-[#C97B5A] transition-colors">
                About
              </button>
              <button onClick={() => setDiscoveryOpen(true)} className="px-6 py-2 bg-[#C97B5A] text-white rounded-md hover:bg-[#b86a4a] transition-colors">
                Get Started
              </button>
              <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">Login</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Client Login</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#C97B5A] hover:bg-[#b86a4a]" disabled={loading}>
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Discovery Call Popup */}
              <Dialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl">Schedule a Discovery Call</DialogTitle>
                    <p className="text-sm text-gray-500">Tell us about your family and we'll reach out to schedule a free, no-pressure call.</p>
                  </DialogHeader>
                  <form onSubmit={handleDiscoveryCall} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="dc-parent">Your Name *</Label>
                        <Input id="dc-parent" data-testid="dc-parent-name" placeholder="Your full name" value={discoveryForm.parent_name} onChange={(e) => setDiscoveryForm({ ...discoveryForm, parent_name: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="dc-email">Email *</Label>
                        <Input id="dc-email" data-testid="dc-email" type="email" placeholder="your@email.com" value={discoveryForm.email} onChange={(e) => setDiscoveryForm({ ...discoveryForm, email: e.target.value })} required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="dc-phone">Phone Number *</Label>
                      <Input id="dc-phone" data-testid="dc-phone" type="tel" placeholder="(555) 123-4567" value={discoveryForm.phone} onChange={(e) => setDiscoveryForm({ ...discoveryForm, phone: e.target.value })} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="dc-child">Child's Name *</Label>
                        <Input id="dc-child" data-testid="dc-child-name" placeholder="Child's first name" value={discoveryForm.child_name} onChange={(e) => setDiscoveryForm({ ...discoveryForm, child_name: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="dc-age">Child's Age *</Label>
                        <Input id="dc-age" data-testid="dc-child-age" type="number" min="0" max="18" placeholder="Age" value={discoveryForm.child_age} onChange={(e) => setDiscoveryForm({ ...discoveryForm, child_age: e.target.value })} required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="dc-concerns">Main Concerns *</Label>
                      <Textarea id="dc-concerns" data-testid="dc-concerns" rows={3} placeholder="What's bringing you here? Tell us about your child and what you're looking for help with." value={discoveryForm.main_concerns} onChange={(e) => setDiscoveryForm({ ...discoveryForm, main_concerns: e.target.value })} required />
                    </div>
                    <div>
                      <Label htmlFor="dc-time">Best Time to Call *</Label>
                      <Input id="dc-time" data-testid="dc-best-time" placeholder="e.g. Weekday mornings 9-11am" value={discoveryForm.best_time_to_call} onChange={(e) => setDiscoveryForm({ ...discoveryForm, best_time_to_call: e.target.value })} required />
                    </div>
                    <Button type="submit" data-testid="dc-submit-btn" className="w-full bg-[#C97B5A] hover:bg-[#b86a4a] text-lg py-5" disabled={loading}>
                      {loading ? 'Submitting...' : 'Request Discovery Call'}
                    </Button>
                    <p className="text-xs text-center text-gray-400">Robin will reach out within 24-48 hours to schedule your call.</p>
                  </form>
                </DialogContent>
              </Dialog>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - Simplified */}
      <section id="hero" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E2D3D] via-[#2a3d4f] to-[#1E2D3D] opacity-95"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOFMwIDguMDYgMCAxOHM4LjA2IDE4IDE4IDE4IDE4LTguMDYgMTgtMTh6IiBmaWxsPSIjQzlBODRDIiBvcGFjaXR5PSIuMDUiLz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Badge className="mb-6 bg-[#C9A84C] text-[#1E2D3D] text-sm px-4 py-2">Now Enrolling</Badge>
            <h1 className="text-6xl md:text-7xl font-bold mb-8 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Your child is waiting to be unboxed.
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Transform your child's life and your family's through expert coaching, supportive community, and actionable strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#C97B5A] hover:bg-[#b86a4a] text-white text-lg px-8 py-6"
                onClick={() => setDiscoveryOpen(true)}
                data-testid="hero-get-started-btn"
              >
                Get Started
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                onClick={() => scrollToSection('program')}
              >
                Learn More
              </Button>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-8 mt-20 max-w-2xl mx-auto">
              <div>
                <div className="text-4xl font-bold text-[#C9A84C]">20+</div>
                <div className="text-sm text-gray-300">Years Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#C9A84C]">7</div>
                <div className="text-sm text-gray-300">Core Pillars</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#C9A84C]">1:1</div>
                <div className="text-sm text-gray-300">Personal Coaching</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different - Condensed */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <p className="text-sm uppercase tracking-wider text-[#C97B5A] text-center mb-4">Why Choose Unbox'd</p>
            <h2 className="text-3xl font-bold text-center mb-12 text-[#1E2D3D]">Professional Expertise + Lived Experience</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CheckCircle className="w-8 h-8 text-[#8FAF8A] mb-2" />
                  <CardTitle className="text-lg">SLP-A Experience & Master's in Special Education</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Decades of experience in pediatric home health and school settings</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CheckCircle className="w-8 h-8 text-[#8FAF8A] mb-2" />
                  <CardTitle className="text-lg">Autism Mom</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Real-world navigation of schools, therapies, health, and daily life with a 10-year-old</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CheckCircle className="w-8 h-8 text-[#8FAF8A] mb-2" />
                  <CardTitle className="text-lg">Out-of-the-Box Approaches</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Primitive reflexes, visual exercises, ILS, sensory integration, and more</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* The Program Section */}
      <section id="program" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <p className="text-sm uppercase tracking-wider text-[#C97B5A] text-center mb-4">The Program</p>
          <h2 className="text-5xl font-bold text-center mb-6 text-[#1E2D3D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            One program. Seven pillars. Your family's full picture.
          </h2>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16">
            This isn't one-size-fits-all. It's collaborative, investigative coaching — we dig into your child's unique situation, find root causes, and build solutions tailored to your family.
          </p>

          {/* Program Card */}
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="border-2 border-[#C9A84C]">
              <CardHeader className="bg-gradient-to-r from-[#1E2D3D] to-[#2a3d4f] text-white">
                <Badge className="mb-2 w-fit bg-[#C9A84C] text-[#1E2D3D]">Signature Program</Badge>
                <CardTitle className="text-3xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Unbox'D Coaching Program
                </CardTitle>
                <CardDescription className="text-gray-300">
                  1-on-1 Family Coaching + Full App Access
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Think of this as having a knowledgeable guide, problem-solver, and partner — all in one. We work through your family's unique challenges across every pillar and find the path forward that actually fits your child's life.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {[
                    'Monthly 1-on-1 coaching sessions',
                    'Sessions scheduled at your convenience',
                    'Access to all program apps, guides, trackers and tools',
                    'Private community group access',
                    'Collaborative problem-solving & detective work',
                    'Group questions answered within 48 business hours'
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <CheckCircle className="w-5 h-5 text-[#8FAF8A] flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-[#FAF7F2] rounded-lg mb-6 italic text-sm text-gray-700">
                  "Not sure if this program is right for your family? Book a free discovery call — let's talk through where you are and where you want to go."
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button className="bg-[#C97B5A] hover:bg-[#b86a4a]" onClick={() => setDiscoveryOpen(true)}>
                    Get Started
                  </Button>
                  <Button variant="outline" onClick={() => scrollToSection('app-tools')}>
                    Browse App Tools Only
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Seven Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <Card key={idx} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <Icon className="w-10 h-10 text-[#C97B5A] mb-3" />
                    <CardTitle className="text-xl">{pillar.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{pillar.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Detective Approach */}
          <div className="bg-[#1E2D3D] text-white rounded-2xl p-12 text-center">
            <div className="text-6xl mb-6">🔍</div>
            <p className="text-sm uppercase tracking-wider text-[#C9A84C] mb-4">The Unbox'D Approach</p>
            <h3 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Every family is a mystery worth solving.
            </h3>
            <p className="max-w-3xl mx-auto text-gray-300 leading-relaxed">
              I come into each coaching relationship as a detective — listening deeply, asking the right questions, connecting the dots across all pillars, and helping you uncover solutions specific to your child's situation. No generic advice. No guesswork. Just focused, personalized guidance built around what's actually going on in your family's life.
            </p>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="py-20 bg-[#1E2D3D] text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            A space to share, learn, and ask anything
          </h2>
          <p className="text-center text-gray-300 max-w-3xl mx-auto mb-16">
            An active group community where parents and caregivers exchange knowledge, share experiences, and get real answers — from each other and directly from the coach.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              { icon: '💬', title: 'Peer Knowledge Sharing', desc: 'Share what\'s working, what isn\'t, and what you\'ve learned across services, school systems, and daily life' },
              { icon: '🙋', title: 'Ask the Coach', desc: 'Post questions directly to the group; coach responds within 48 business hours, often sooner' },
              { icon: '🤝', title: 'Real Talk from Real Families', desc: 'Judgment-free support from people who truly understand IEPs, meltdowns, sensory days, and school fights' },
              { icon: '📚', title: 'Resource Exchange', desc: 'Members and coach share tools, articles, and insights on primitive reflexes, auditory processing, self-regulation, and more' }
            ].map((item, idx) => (
              <Card key={idx} className="bg-white/10 border-white/20 text-white">
                <CardHeader>
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <CardTitle>{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-[#C9A84C] text-[#1E2D3D] rounded-lg p-6 text-center">
            <p className="font-semibold">⏱️ Coach Response Commitment — all group questions answered within 48 business hours, often much sooner</p>
          </div>
        </div>
      </section>

      {/* App Tools Section */}
      <section id="app-tools" className="py-20 bg-[#FAF7F2]">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center mb-6 text-[#1E2D3D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Standalone tools for families on their own path
          </h2>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16">
            Not ready for 1-on-1 coaching? Access individual app tools à la carte — practical, experience-based support right in your hands.
          </p>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              { icon: Brain, title: 'Primitive Reflex App', desc: 'Identify & address retained reflexes affecting behavior, sensory & learning', featured: true },
              { icon: MessageCircle, title: 'Communication Support Guide', desc: 'Strategies for all communication styles and needs' },
              { icon: Leaf, title: 'Sensory & Diet Tracker', desc: 'Track sensory responses, food patterns, and triggers' },
              { icon: School, title: 'IEP Navigation Guide', desc: 'Understand the process, prep for meetings, track services' },
              { icon: Home, title: 'Daily Life Strategy Bank', desc: 'Routines, transitions, and regulation tips for real family life', isNew: true }
            ].map((tool, idx) => {
              const Icon = tool.icon;
              return (
                <Card key={idx} className={`${tool.featured ? 'border-l-4 border-[#C9A84C]' : ''}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Icon className="w-8 h-8 text-[#C97B5A]" />
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {tool.title}
                            {tool.featured && <Badge className="bg-[#C9A84C] text-[#1E2D3D]">Featured</Badge>}
                            {tool.isNew && <Badge variant="outline">New</Badge>}
                          </CardTitle>
                          <CardDescription>{tool.desc}</CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" className="bg-[#C97B5A] hover:bg-[#b86a4a]" onClick={() => setDiscoveryOpen(true)}>
              Get Started
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_repo-duplicate-11/artifacts/5pbmteav_F4019F17-444B-440C-A302-7A83B4239691.png"
                alt="Robin - Your Coach & Guide"
                className="rounded-2xl shadow-2xl w-full"
              />
              <Badge className="absolute top-4 right-4 bg-[#C9A84C] text-[#1E2D3D] text-sm px-4 py-2">20+ Years Experience</Badge>
            </div>

            <div>
              <p className="text-sm uppercase tracking-wider text-[#C97B5A] mb-4">About Your Coach</p>
              <h2 className="text-4xl font-bold mb-6 text-[#1E2D3D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Meet Robin
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  I'm an autism coach and consultant with an SLP-A background and decades of experience in pediatric home health and school settings. That experience, combined with a Master's in Special Education I pursued specifically to support my own son, gives me a uniquely layered lens for guiding families navigating autism.
                </p>
                <p>
                  My 10-year-old son is autistic, and through him I've gained knowledge no degree alone could provide. I've personally navigated primitive reflex integration, visual exercises, experience implementing ILS, and sensory integration approaches — not just as a professional, but as a mom fully in it every single day.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-8">
                {[
                  'Autism Coach & Consultant',
                  'SLP-A Background',
                  'Special Education (M.Ed.)',
                  'IEP Navigation',
                  'Primitive Reflex Integration',
                  'Experience Implementing ILS',
                  'Visual & Auditory Processing',
                  'Traditional & Non-Traditional Therapy Knowledge',
                  'Sensory Integration',
                  'Neurodivergent Family Living'
                ].map((chip, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs py-2 justify-center">
                    {chip}
                  </Badge>
                ))}
              </div>
              <div className="p-6 bg-[#FAF7F2] rounded-lg border-l-4 border-[#8FAF8A] italic">
                <p className="text-[#1E2D3D]">
                  "Our family is neurodivergent through and through — from autism to ADHD, and myself as an undiagnosed adult. This isn't just my career. It is my life. And that's exactly why I can help yours."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E2D3D] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Unbox'D Autism
              </h3>
              <p className="text-sm uppercase tracking-wider text-[#C97B5A] mb-4">Coaching & Consulting</p>
              <p className="text-gray-400 text-sm">
                Professional expertise. Lived understanding. Real solutions for your family.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <button onClick={() => scrollToSection('program')} className="text-left hover:text-[#C97B5A] transition-colors">Program</button>
                <button onClick={() => scrollToSection('community')} className="text-left hover:text-[#C97B5A] transition-colors">Community</button>
                <button onClick={() => scrollToSection('app-tools')} className="text-left hover:text-[#C97B5A] transition-colors">App Tools</button>
                <button onClick={() => scrollToSection('about')} className="text-left hover:text-[#C97B5A] transition-colors">About</button>
                <button onClick={() => scrollToSection('discovery')} className="text-left hover:text-[#C97B5A] transition-colors">Get Started</button>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-400 space-y-4">
            <p className="text-xs text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Disclaimer: Unbox'D Autism provides parent coaching and consulting services only. Robin has a background as an SLP-A and holds a Master's in Special Education, but she is not providing therapy, medical, or legal services through this platform. All information and guidance shared through coaching sessions, community discussions, and app tools are based on personal and professional experience and are for informational and educational purposes only. They do not constitute medical, therapeutic, or legal advice. Always consult qualified professionals for medical, therapeutic, or legal matters.
            </p>
            <p>© 2026 Unbox'D Autism. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLanding;
